import { Component, Input, OnInit } from '@angular/core';
import { CaseServiceInfo, InsuranceCase } from '../../../app-create-case/models/insurance-case.model';
import { DocumentInfo } from '../../models/domain/DocumentInfo';
import { takeUntil } from 'rxjs/operators';
import { SeedMetaData } from '../../models/domain/SeedMetaData';
import { TotalNewPricePipe } from '../../../app-cases/pipes/total-new-price.pipe';
import { ServiceProviderInfo } from '../../models/service-provider-info-model';
import {
    CaseDocumentTypesSummary,
    ManagementSummaryModel,
    ServiceProviderSummary
} from '../../models/ManagementSummaryModel';
import { PCXConstants } from '../../../sc-config/constant/PCXConstants';
import { DataStoreService } from '../../services/data-store.service';
import { TranslateSeedDataPipe } from '../../pipes/translate-seed-data.pipe';
import { ServiceProviderService } from '../../services/service-provider.service';
import { catchError, forkJoin, of, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { IpexServiceProviderCount } from '../../models/IpexServiceProviderCountModel';
import { CommonService } from '../../services/common.service';
import { FilterCaseDocumentListPipe } from '../../pipes/Filter-Case-Document-List.pipe';
import { SCConfig } from '../../../sc-config/constant/sc-config.constant';
import { CaseService } from '../../services/case.service';
import { FormArray, FormGroup } from '@angular/forms';
import { CaseCommonService } from '../../../app-create-case/services/case-common.service';

@Component({
    selector: 'app-management-summary',
    templateUrl: './app-management-summary.component.html',
    styleUrls: ['./app-management-summary.component.scss'],
    standalone: false
})
export class AppManagementSummaryComponent implements OnInit {
    protected readonly CaseDocumentTypes = PCXConstants.CaseDocumentTypes;
    managementSummaryInfo = new ManagementSummaryModel();
    serviceAddress: string = '';
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();
    public isServiceProviderLoading: boolean = false;
    public isCheckedCaseLoading: boolean = false;

    @Input() insuranceCase: InsuranceCase;
    @Input() caseCreationForm: FormGroup;

    constructor(
        private readonly dataStoreService: DataStoreService,
        private readonly serviceProviderService: ServiceProviderService,
        private readonly translateService: TranslateService,
        private readonly caseService: CaseService,
        private readonly caseCommonService: CaseCommonService
    ) { }

    ngOnInit() {
        if (this.insuranceCase) {
            this.getManagementSummary(this.insuranceCase);
            this.serviceAddress = this.getFormattedAddress();
        }

        // update document summary after add/remove documents
        this.caseCommonService.updatedCaseDocumentInfo$.pipe(takeUntil(this.destroy$)).subscribe(documentInfoList => {
            if (documentInfoList) {
                this.getCaseDocumentSummary(documentInfoList);
            }
        });

        // Update Trades and new total price on add/update service item
        const caseServiceListArray = this.caseCreationForm?.get('CaseServiceList') as FormArray;
        if (caseServiceListArray) {
            caseServiceListArray.valueChanges
                .pipe(takeUntil(this.destroy$))
                .subscribe(value => {
                    this.updateSummaryOfTradesAndNewSubTotalPrices(caseServiceListArray);
                });
        }

        // update prices summary on enable/disable service item
        if (caseServiceListArray?.controls?.length === 0) {
            caseServiceListArray.controls.forEach((group, index) => {
                if (!(group instanceof FormGroup)) return;

                group.get('IsServiceActive')?.valueChanges.pipe(takeUntil(this.destroy$))
                    .subscribe(value => {
                        const caseServiceList: CaseServiceInfo[] = caseServiceListArray.value;
                        caseServiceList[index].IsServiceActive = value;
                        console.log(caseServiceList);
                        this.getTotalPriceOfServiceItems(caseServiceList);
                    });
            });
        }

        // update management summary after remove item
        this.caseCommonService.deletedItem$.pipe(takeUntil(this.destroy$)).subscribe(caseServiceInfo => {
            if (caseServiceInfo) {
                const caseServiceList: CaseServiceInfo[] = this.caseCreationForm.get('CaseServiceList').value;
                if (caseServiceList?.length === 0) return;
                const newServiceListAfterItemRemove = caseServiceList.filter(item => item != caseServiceInfo);

                // Removed Deleted Trade
                this.getServiceTrades(newServiceListAfterItemRemove);

                // Add new total prices after removed item
                this.getTotalPriceOfServiceItems(newServiceListAfterItemRemove);

                //Remove deleted service provider details from summary
                if (caseServiceList.length > 0) {
                    const totalItemOfServiceProvider = caseServiceList.filter(
                        item =>
                            item?.ServiceAdditionalData?.ServiceProviderItemId ==
                            caseServiceInfo?.ServiceAdditionalData?.ServiceProviderItemId
                    );
                    if (totalItemOfServiceProvider.length == 1) {
                        this.managementSummaryInfo.serviceProvideDetails =
                            this.managementSummaryInfo.serviceProvideDetails.filter(
                                item =>
                                    item.serviceProviderInfo?.serviceProvider?.id !=
                                    caseServiceInfo?.ServiceAdditionalData?.ServiceProviderItemId
                            );
                    }
                }
            }
        });
    }

    // update Total Calculated amount while add/update existing item
    updateSummaryOfTradesAndNewSubTotalPrices(parentCaseServiceListArray: FormArray) {
        if (parentCaseServiceListArray?.controls?.length === 0) return;
        const caseServiceListArray = this.caseCreationForm.get('CaseServiceList') as FormArray;

        // Update Trades summary while edit/remove existing trade item
        caseServiceListArray.controls.forEach((group, index) => {
            group
                .get('ServiceAdditionalData.CaseTradeId')
                ?.valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe(value => {
                    const caseServiceList: CaseServiceInfo[] = caseServiceListArray.value;
                    caseServiceList[index].ServiceAdditionalData.CaseTradeId = value;
                    this.getServiceTrades(caseServiceList);
                });
        });

        // update price summary on add/update summary
        caseServiceListArray.controls.forEach((group, index) => {
            group.get('NewSubTotalPrice')?.valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe(value => {
                    this.getTotalPriceOfServiceItems(caseServiceListArray.value);
                });
        });
    }

    public getFormattedAddress(): string {
        let fullAddress = `${this.insuranceCase?.ObjectStreet ?? ''} ${this.insuranceCase?.ObjectStreetNumber ?? ''},
                           ${this.insuranceCase?.ZipCodeOfIssuer ?? ''} ${this.insuranceCase?.ObjectCity ?? ''}`.trim();

        if (fullAddress.length === 0) return fullAddress;

        if (fullAddress[0] === ',') {
            fullAddress = fullAddress.substring(1);
        }

        if (fullAddress[fullAddress.length - 1] === ',') {
            fullAddress = fullAddress.substring(0, fullAddress.length - 1);
        }
        return fullAddress.trim();
    }

    getManagementSummary(insuranceCase: InsuranceCase) {
        // Case Document Summary
        if (insuranceCase.CaseDocumentList.length > 0) {
            this.getCaseDocumentSummary(insuranceCase.CaseDocumentList);
        }

        // Case Trades summary
        this.getServiceTrades(insuranceCase?.CaseServiceList);

        // case service total amount summary
        this.getTotalPriceOfServiceItems(insuranceCase?.CaseServiceList);

        // service provide summary
        this.getServiceProviderSummary(insuranceCase.CaseServiceList);
    }

    getServiceTrades(caseServiceList: CaseServiceInfo[]) {
        this.dataStoreService.caseTrades.pipe(takeUntil(this.destroy$)).subscribe((caseTrades: SeedMetaData[]) => {
            if (caseTrades) this.getServiceTradesSummary(caseServiceList, caseTrades);
        });
    }

    getServiceTradesSummary(caseServiceList: CaseServiceInfo[], caseTrades: SeedMetaData[]) {
        const translateSeedDataPipe = new TranslateSeedDataPipe();
        const currentDesktopLanguage = this.translateService.currentLang;
        let trades = [];
        caseServiceList.forEach((caseServiceInfo: CaseServiceInfo) => {
            if (caseServiceInfo.ServiceAdditionalData?.CaseTradeId) {
                const serviceTrade = translateSeedDataPipe.transform(
                    caseServiceInfo?.ServiceAdditionalData?.CaseTradeId,
                    currentDesktopLanguage,
                    caseTrades,
                    false
                );
                trades.push(serviceTrade);
            }
        });
        this.managementSummaryInfo.stringTrades = trades.join(', ') || '';
    }

    getServiceProviderSummary(caseServiceList: CaseServiceInfo[]) {
        this.isServiceProviderLoading = true;
        if (caseServiceList?.length === 0) {
            this.isServiceProviderLoading = false;
            return;
        }
        const uniqueServiceProviderItemIds = [
            ...new Set(caseServiceList.map(caseService => caseService?.ServiceAdditionalData?.ServiceProviderItemId))
        ];
        const observables = uniqueServiceProviderItemIds.map((serviceProviderItemId: string) => {
            if (!serviceProviderItemId) {
                return of(null); // Return an empty observable for invalid items
            }

            return this.serviceProviderService.getServiceProviderDetail(serviceProviderItemId).pipe(
                catchError(error => {
                    console.error(`Error fetching details for ID ${serviceProviderItemId}:`, error);
                    return of(null); // Handle error and continue with the rest
                })
            );
        });

        forkJoin(observables)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (serviceProviderInfos: (ServiceProviderInfo | null)[]) => {
                    serviceProviderInfos.forEach(serviceProviderInfo => {
                        if (serviceProviderInfo?.serviceProvider?.id) {
                            const serviceProviderSummary = new ServiceProviderSummary(serviceProviderInfo, null);
                            this.managementSummaryInfo.serviceProvideDetails.push(serviceProviderSummary);
                        }
                    });
                },
                complete: () => {
                    const serviceProvideItemIds = this.managementSummaryInfo.serviceProvideDetails.map(
                        serviceProvideDetail => serviceProvideDetail.serviceProviderInfo?.serviceProvider?.id
                    );
                    this.isServiceProviderLoading = false;
                    if (serviceProvideItemIds?.length === 0) return;
                    this.getServiceProvideSummary(serviceProvideItemIds);
                },
                error: err => {
                    this.isServiceProviderLoading = false;
                    console.error('Error during data fetching:', err);
                }
            });
    }

    getServiceProvideSummary(serviceProviderIds: string[]) {
        this.isCheckedCaseLoading = true;
        this.serviceProviderService
            .getServiceProviderCount(serviceProviderIds)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (ipexServiceProviderCounts: IpexServiceProviderCount[]) => {
                    if (ipexServiceProviderCounts?.length > 0) {
                        const serviceProvideDetails = this.managementSummaryInfo.serviceProvideDetails;
                        const ipexServiceProviderCountMap = new Map<string, IpexServiceProviderCount>();

                        ipexServiceProviderCounts.forEach(ipexServiceProviderCount => {
                            if (ipexServiceProviderCount.connectedCaseCount == null)
                                ipexServiceProviderCount.connectedCaseCount = 0;
                            ipexServiceProviderCountMap.set(
                                ipexServiceProviderCount.serviceProviderId,
                                ipexServiceProviderCount
                            );
                        });

                        serviceProvideDetails.forEach(serviceProviderSummary => {
                            const serviceProviderId = serviceProviderSummary.serviceProviderInfo?.serviceProvider?.id;
                            if (serviceProviderId && ipexServiceProviderCountMap.has(serviceProviderId)) {
                                serviceProviderSummary.ipexServiceProviderCount =
                                    ipexServiceProviderCountMap.get(serviceProviderId)!;
                            }
                        });
                    } else {
                        this.isCheckedCaseLoading = false;
                    }
                },
                complete: () => {
                    this.isCheckedCaseLoading = false;
                },
                error: err => {
                    this.isCheckedCaseLoading = false;
                }
            });
    }

    getTotalPriceOfServiceItems(caseServiceList: CaseServiceInfo[]) {
        const totalCalculatedPrice = new TotalNewPricePipe().transform(caseServiceList);
        this.managementSummaryInfo.totalAmount = totalCalculatedPrice?.totalNewPrice;
    }

    getCaseDocumentSummary(caseDocumentInfo: DocumentInfo[]) {
        this.managementSummaryInfo.caseDocumentTypesSummary = new CaseDocumentTypesSummary();
        const caseDocumentListOfCaseFile = new FilterCaseDocumentListPipe(this.caseService).transform(
            caseDocumentInfo,
            SCConfig.DOWNLOAD_TYPE.CASE_FILES
        );
        caseDocumentListOfCaseFile.forEach((documentInfo: DocumentInfo) => {
            if (documentInfo?.DocumentType === this.CaseDocumentTypes.OFFER_TYPE) {
                this.managementSummaryInfo.caseDocumentTypesSummary.Offers++;
            } else if (documentInfo?.DocumentType === this.CaseDocumentTypes.INVOICE_TYPE) {
                this.managementSummaryInfo.caseDocumentTypesSummary.Invoices++;
            } else if (documentInfo?.DocumentType === this.CaseDocumentTypes.PHOTO_TYPE) {
                this.managementSummaryInfo.caseDocumentTypesSummary.Photos++;
            } else {
                this.managementSummaryInfo.caseDocumentTypesSummary.Misc++;
            }
        });
    }
}
